<template>
	<div class="bg-white border rounded-lg p-4">
		<div class="mb-4">
			<h6 class="text-lg uppercase tracking-wider text-gray-500 mb-1">
				Preturi de baza
			</h6>
			<v-label class="text-base">
				Preturile de baza stocate in configuratorul nou, pe tara.
				<span class="font-bold">*Preturi fara TVA</span> — discounturile si taxele se aplica peste ele.
				<span class="block mt-2">
					Stalpii se calculeaza pe <span class="font-bold">metru liniar (/ml)</span>, dupa inaltimea
					stalpului. Restul elementelor se calculeaza pe <span class="font-bold">metru patrat (/m²)</span>.
				</span>
			</v-label>
		</div>

		<v-form v-model="validForm" ref="formBasePrices" @submit.prevent="save">
			<table class="w-full max-w-3xl mb-4">
				<thead>
					<tr class="text-left text-gray-500">
						<th class="pb-2 w-4/12">Element</th>
						<th v-for="country in countries" :key="country.code" class="pb-2">
							<div class="flex items-center">
								<span class="w-5 h-5 mr-2">{{ country.flag }}</span>
								<span>{{ country.name }}</span>
							</div>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="price in priceKeys" :key="price">
						<td class="py-1 pr-2 align-middle">
							{{ labels[price] }}
							<span class="text-gray-500">{{ unitFor(price) }}</span>
						</td>
						<td v-for="country in countries" :key="country.code" class="py-1 pr-2 align-middle">
							<v-text-field v-model="prices[country.code][price]" dense type="number" min="0" step="1"
								hide-spin-buttons outlined suffix="eur" hide-details="auto"
								:rules="[rules.required, rules.notNegative]"></v-text-field>
						</td>
					</tr>
				</tbody>
			</table>

			<div class="flex justify-end">
				<v-btn type="submit" color="teal" class="uppercase white--text" :loading="saving"
					:disabled="saving || !hasChanges">Salveaza</v-btn>
			</div>
		</v-form>
	</div>
</template>

<script>
import flags from "@/assets/countries-flags.json"
import pricingSection from './pricingSection';

export default {
	name: "BasePrices",
	mixins: [pricingSection],
	data() {
		return {
			validForm: false,
			// the columns the API accepts, in display order (no price_panel_frame, the configurator has no module for it)
			priceKeys: [
				'price_panel',
				'price_gate_pedestrian',
				'price_gate_swing',
				'price_gate_swing_bifold',
				'price_gate_slide',
				'price_gate_slide_telescopic',
				'price_gate_slide_articulated',
				'price_gate_selfsupport',
				'price_post_80',
				'price_post_100',
				'price_post_200'
			],
			labels: {
				price_panel: 'Panou',
				price_gate_pedestrian: 'Poarta pietonala',
				price_gate_swing: 'Poarta batanta',
				price_gate_swing_bifold: 'Poarta batanta bi-fold',
				price_gate_slide: 'Poarta culisanta',
				price_gate_slide_telescopic: 'Poarta culisanta telescopica',
				price_gate_slide_articulated: 'Poarta culisanta articulata',
				price_gate_selfsupport: 'Poarta autoportanta',
				price_post_80: 'Stalp 80',
				price_post_100: 'Stalp 100',
				price_post_200: 'Stalp 200'
			},
			prices: {},
			rules: {
				required: value => (value !== '' && value !== null && value !== undefined) || 'Câmp obligatoriu',
				notNegative: value => (!isNaN(parseFloat(value)) && parseFloat(value) >= 0) || 'Valoare invalida',
			},
		};
	},
	computed: {
		saving() {
			return this.section.savingBasePrices;
		},
		basePrices() {
			return this.section.basePrices;
		},
		countries() {
			return Object.keys(this.basePrices).map(code => ({
				code: code,
				name: flags[code] ? flags[code].name : code,
				flag: flags[code] ? flags[code].emoji : ''
			}));
		},
		hasChanges() {
			return Object.keys(this.buildChanges()).length > 0;
		}
	},
	methods: {
		/**
		 * What the price is charged against. Posts are cut from stock to the height a run needs, so
		 * they go by the metre; everything else is built to a size and goes by the square metre.
		 *
		 * Read off the key so a post size added later is covered without touching this.
		 */
		unitFor(key) {
			return String(key).indexOf("price_post_") === 0 ? "/ml" : "/m²";
		},
		storedPrice(code, price) {
			const stored = this.basePrices[code][price];
			return stored === null || stored === undefined ? null : Number(stored);
		},
		syncFromStore() {
			const prices = {};
			Object.keys(this.basePrices).forEach(code => {
				prices[code] = {};
				this.priceKeys.forEach(price => {
					const stored = this.storedPrice(code, price);
					prices[code][price] = stored === null ? '' : stored;
				});
			});
			this.prices = prices;
		},
		// only the columns that actually changed are sent, per country - the endpoint merges them
		// over what is stored, so untouched columns keep their value
		buildChanges() {
			const changes = {};

			Object.keys(this.prices).forEach(code => {
				const dirty = {};

				this.priceKeys.forEach(price => {
					const value = Number(parseFloat(this.prices[code][price]).toFixed(2));
					if (value !== this.storedPrice(code, price)) dirty[price] = value;
				});

				if (Object.keys(dirty).length > 0) changes[code] = dirty;
			});

			return changes;
		},
		async save() {
			const valid = await this.$refs.formBasePrices.validate();

			if (valid) {
				this.dispatchSection('saveBasePrices', this.buildChanges());
			}
		}
	},
	watch: {
		basePrices: {
			handler: 'syncFromStore',
			deep: true
		}
	},
	created() {
		this.syncFromStore();
	}
};
</script>

<style scoped></style>
